/**
 * ANA-R1-13 — "don't ask again for this data set" suppression.
 *
 * The dataset-open download prompt (DatasetDownloadPrompt) offers a "don't ask
 * again for this data set" checkbox. When ticked, we must never re-prompt for
 * THAT data set, for THAT user, on THIS browser.
 *
 * ## Why localStorage (not the Lyse preferences blob)
 * This is a per-browser-profile UI preference, exactly like the cache budget
 * (`storageBudget.ts`) and the filter collapse state (`useFilterCollapse.ts`).
 * The Lyse preferences blob round-trips to the auth server and its Zod schema
 * `.strip()`s unknown keys, so adding a field there would be a wire-shape /
 * contract change — which the story forbids ("do NOT invent a BE preferences
 * call"). The cache work (CACHE-03) already set the precedent of keeping
 * cache-adjacent prefs browser-local; the suppression follows it.
 *
 * ## Key shape — per user + per data set
 * One localStorage entry holds a JSON map of `"<userId>::<daId>" -> true`.
 * Scoping by user as well as data set keeps two analysts sharing one browser
 * profile from inheriting each other's suppression. The pre-login fallback id
 * is `anonymous` (mirrors `AnalysisLayoutMount`'s workspace keying), so a
 * decision made before `/me` settles is re-evaluated once the real id arrives.
 *
 * The module exposes a tiny subscribable store so the prompt component reads a
 * single source of truth and re-renders when suppression flips.
 */

const STORAGE_KEY =
  "@aeriosiq/lyse/cache/dataset-download-prompt/suppressed/v1";

export const ANONYMOUS_USER_ID = "anonymous";

/** Compose the per-user + per-data-set suppression key. */
export function suppressionKey(
  userId: string | null | undefined,
  dataAcquisitionId: string,
): string {
  const id = userId && userId.length > 0 ? userId : ANONYMOUS_USER_ID;
  return `${id}::${dataAcquisitionId}`;
}

function readPersisted(): Record<string, boolean> {
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    const out: Record<string, boolean> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (v === true) out[k] = true;
    }
    return out;
  } catch {
    return {};
  }
}

class DatasetDownloadPromptPrefsStore {
  private suppressed: Record<string, boolean> = readPersisted();
  private listeners = new Set<() => void>();

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };

  /** Stable snapshot (referentially stable until a mutation). */
  getSnapshot = (): Record<string, boolean> => this.suppressed;

  /** Is the prompt suppressed for this user + data set? */
  isSuppressed(
    userId: string | null | undefined,
    dataAcquisitionId: string,
  ): boolean {
    return this.suppressed[suppressionKey(userId, dataAcquisitionId)] === true;
  }

  /** Persist "don't ask again" for this user + data set. */
  suppress(userId: string | null | undefined, dataAcquisitionId: string): void {
    const key = suppressionKey(userId, dataAcquisitionId);
    if (this.suppressed[key] === true) return;
    this.suppressed = { ...this.suppressed, [key]: true };
    this.persist();
    this.emit();
  }

  private persist(): void {
    if (typeof localStorage === "undefined") return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.suppressed));
    } catch {
      /* private mode / quota — the in-memory value still drives this session */
    }
  }

  private emit(): void {
    for (const fn of this.listeners) fn();
  }

  /** @internal — reset to empty (tests). */
  __reset(): void {
    this.suppressed = {};
    if (typeof localStorage !== "undefined") {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }
    this.emit();
  }

  /** @internal — re-read from localStorage (tests). */
  __reload(): void {
    this.suppressed = readPersisted();
    this.emit();
  }
}

export const datasetDownloadPromptPrefs = new DatasetDownloadPromptPrefsStore();
